import { Link, navigate } from "gatsby"
import _ from "lodash"
import React, { useCallback, useEffect, useState } from "react"
import styled, { css } from "styled-components"
import Button from "../../atoms/button"
import Divider from "../../atoms/divider"
import { Block, Spacer } from "../../atoms/layout-primitives"
import { BlogGrid } from "../../atoms/layout-primitives/grid"
import TagSelect, { SelectCaret } from "../../atoms/tag-select"
import VariantContainer from "../../atoms/variant-container"
import ArticleRowTitle from "../../molecules/article-row-title"
import TagFilter from "../../molecules/tag-filter"
import ArticleCardGrid from "../../organisms/article-card-grid"
import { FadeIn } from "../../styles/animations.styles"
import { BT } from "../../styles/blog-typography"
import { IContentfulTag } from "../../types/blog.types"
import InternalRoutes from "../../util/internal-routes"

const TabButton = styled.button<{ $active: boolean }>`
  ${BT.sm.info`400`};
  list-style: none;
  font-size: 16px;
  padding: 8px 16px;
  background-color: ${(props) =>
    props.$active ? "var(--burnt-orange)" : "var(--white)"};
  color: ${(props) => (props.$active ? "var(--white)" : "var(--slate)")};
  border: 1px solid
    ${(props) => (props.$active ? "var(--burnt-orange)" : "#d9d9d9")};
  border-radius: 2px 2px 0px 0px;
  cursor: pointer;
  :hover,
  :focus,
  :active {
    background-color: var(--burnt-orange);
    color: var(--white);
  }

  transition: all 0.2s;
`

const TabletSeeAllLink = styled(Link)`
  text-decoration: none;
`

/**
 * Handles the tab click which is a route change under the hood
 * @param tabNumber the tab/page number to move to
 */
const handleTabClick = (tabNumber: number) => {
  navigate(`${InternalRoutes.Blog()}${tabNumber > 1 ? tabNumber : ``}`, {
    replace: true, // Don't clutter up history with tab changes
    state: {
      // Maintain scroll position so user doesn't realize they've moved routes
      disableScrollUpdate: true,
    },
  })
}

/** Tags for the All tab */
const AllTag = { name: "All", slug: "all" }
/** Page number adjustment for tabs */
const adjustmentValue = 2

interface ILatestMediaProps {
  tags: any
  articles: any
  currentTab: number
  currentCategory: IContentfulTag
}

/**
 * Displays the 6 of the latest articles for each topic within each category
 * @param tags - the tags to display in the format of `{ <Category>: [ <Tag> ] }`
 * @param articles - the articles to display in categorized format of `{ <Category>: [ <Topic>: [Articles] ] }`
 * @param currentTab - the current tab/page number
 * @param currentCategory - the current category
 * @returns
 */
const LatestMedia = ({
  tags,
  articles,
  currentTab,
  currentCategory,
}: ILatestMediaProps) => {
  const categories: IContentfulTag[] = Object.keys(tags).map((categoryName) => {
    return { name: categoryName, slug: _.kebabCase(categoryName.toLowerCase()) }
  })
  const [activeTopic, setActiveTopic] = useState(AllTag.name)
  const [isAnimating, setIsAnimating] = React.useState(false)

  const handleTopicFilterClick = useCallback(
    (topicName: string) => {
      // Cancel current animation if one is still running
      setIsAnimating(false)

      // Delay a few milliseconds then run the animation again
      setTimeout(() => {
        setIsAnimating(true)

        // set active topic
        setActiveTopic(topicName)
      }, 10)
    },
    [setIsAnimating, setActiveTopic]
  )

  const onAnimationEnd = useCallback(() => {
    // Reset the animation at the end so it can be triggered again
    setIsAnimating(false)
  }, [setIsAnimating])

  useEffect(() => {
    // Add event listener to detect when animation is complete
    window.addEventListener("animationend", onAnimationEnd)
    return () => {
      window.removeEventListener("animationend", onAnimationEnd)
    }
  }, [])

  return (
    <BlogGrid>
      <Block display={{ sm: "block", md: "none", lg: "block" }}>
        <ArticleRowTitle
          title="Latest Media"
          linkText="See All Media"
          url={InternalRoutes.Blog(AllTag)}
        />
      </Block>
      <Block display={{ sm: "none", md: "block", lg: "none" }}>
        {/* On tablet this whole block is clickable */}
        <TabletSeeAllLink
          to={InternalRoutes.Blog(currentCategory, {
            slug:
              activeTopic !== AllTag.name
                ? _.kebabCase(activeTopic.toLowerCase())
                : "",
          })}
        >
          <ArticleRowTitle
            title="Latest Media"
            linkText="See All"
            url={InternalRoutes.Blog(currentCategory, {
              slug:
                activeTopic !== AllTag.name
                  ? _.kebabCase(activeTopic.toLowerCase())
                  : "",
            })}
          />
        </TabletSeeAllLink>
      </Block>
      <Spacer height={{ sm: "24px" }} />
      <Block>
        {/* Mobile dropdown menu */}
        <Block
          display={{ md: "none" }}
          variants={[
            css`
              label {
                ${BT.sm.info`500`};
                font-size: 12px;
                opacity: 0.45;
                text-transform: uppercase;
              }
            `,
          ]}
        >
          <label htmlFor={"categories"}>Filter Content</label>
          <Spacer height={{ sm: "12px" }} />
        </Block>
        <Block
          display={{ md: "none" }}
          variants={[
            css`
              position: relative;
            `,
          ]}
        >
          <SelectCaret />
          <TagSelect
            id={"categories"}
            name={"categories"}
            value={currentTab}
            onChange={(e) => handleTabClick(parseInt(e.target.value))}
          >
            <option value={1}>{AllTag.name}</option>
            {categories.map((category, idx) => {
              const adjustedIdx = idx + adjustmentValue
              return (
                <option
                  key={`${category.name}-${adjustedIdx}-mobile`}
                  value={adjustedIdx}
                >
                  {category.name}
                </option>
              )
            })}
          </TagSelect>
        </Block>
        {/* Desktop / Tablet tab bar */}
        <Block display={{ sm: "none", md: "block" }}>
          <TabButton
            onClick={() => handleTabClick(1)}
            $active={currentTab === 1}
          >
            {AllTag.name}
          </TabButton>
          {categories.map((category, idx) => {
            const adjustedIdx = idx + adjustmentValue
            return (
              <TabButton
                key={`${category.name}-${adjustedIdx}`}
                onClick={() => handleTabClick(adjustedIdx)}
                $active={currentTab === adjustedIdx}
              >
                {category.name}
              </TabButton>
            )
          })}
          <Divider />
        </Block>
        <Spacer height={{ sm: "16px" }} />
        <VariantContainer
          variants={[
            css`
              animation-name: ${FadeIn};
              animation-duration: 0.5s;
            `,
          ]}
        >
          <TagFilter
            tags={
              currentCategory.name !== AllTag.name
                ? tags[currentCategory.name]
                : []
            }
            id="topics"
            name="Topics"
            onClick={handleTopicFilterClick}
          />
          <Spacer height={{ sm: "24px" }} />
          <VariantContainer
            variants={[
              css`
                animation-name: ${isAnimating ? FadeIn : "none"};
                animation-play-state: ${isAnimating ? "running" : "paused"};
                animation-duration: 0.5s;
              `,
            ]}
          >
            <ArticleCardGrid
              articles={articles[activeTopic]}
              overflow={"carousel"}
              align={"left"}
            />
          </VariantContainer>
        </VariantContainer>
      </Block>
      <Spacer height={{ sm: "32px" }} />
      <Block display={{ md: "none", lg: "block" }} col={{ lg: "5/-5" }}>
        <Button.Link
          to={InternalRoutes.Blog(currentCategory, {
            slug:
              activeTopic !== AllTag.name
                ? _.kebabCase(activeTopic.toLowerCase())
                : "",
          })}
          fullWidth
        >
          See All{" "}
          {activeTopic !== AllTag.name
            ? activeTopic
            : currentCategory.name !== AllTag.name
            ? currentCategory.name
            : ``}{" "}
          Content
        </Button.Link>
      </Block>
      <Spacer height={{ sm: "48px" }} />
    </BlogGrid>
  )
}

export default LatestMedia
